/**
 * Service Worker utilities for caching uploaded files.
 *
 * This module provides functions to:
 * - Register the slides service worker
 * - Cache uploaded files before CDN availability
 * - Clean up old cached files
 */

let swRegistration: ServiceWorkerRegistration | null = null;
let isInitialized = false;

/**
 * Registers the slides service worker and configures it with the media origin.
 * Should be called once on app startup.
 */
export async function registerSlidesSW(mediaOrigin: string): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    console.warn('[SlidesSW] Service workers not supported');
    return;
  }

  if (isInitialized) {
    return;
  }

  try {
    swRegistration = await navigator.serviceWorker.register('/slides-sw.js');

    await navigator.serviceWorker.ready;

    const sw =
      swRegistration.active ??
      swRegistration.waiting ??
      swRegistration.installing;

    if (!sw) {
      console.warn('[SlidesSW] No service worker instance available');
      return;
    }

    // Extract origin from the media URL
    const origin = new URL(mediaOrigin).origin;

    // Configure the SW with the media origin
    sw.postMessage({
      type: 'SET_ORIGIN',
      origin,
    });

    // Clean old cache entries on startup
    sw.postMessage({
      type: 'CLEAN_OLD_CACHE',
    });

    isInitialized = true;
    console.log('[SlidesSW] Service worker registered and configured');
  } catch (error) {
    console.error('[SlidesSW] Registration failed:', error);
  }
}

/**
 * Caches a file in the service worker for the given URL.
 * Returns a promise that resolves when the cache is updated.
 *
 * @param url - The URL that will be used to serve the cached file
 * @param file - The file to cache
 * @param timeoutMs - Timeout for waiting for cache confirmation (default: 8000ms)
 */
export async function cacheFileForUrl(
  url: string,
  file: File,
  timeoutMs = 8000
): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    console.warn('[SlidesSW] Service workers not supported');
    return;
  }

  const reg = await navigator.serviceWorker.ready;
  const sw = reg.active;

  if (!sw) {
    console.warn('[SlidesSW] No active service worker');
    return;
  }

  const arrayBuffer = await file.arrayBuffer();

  // Create a promise that waits for cache confirmation
  const waitForCacheUpdate = new Promise<void>((resolve, reject) => {
    const onMessage = (e: MessageEvent) => {
      if (e.data?.type === 'CACHE_UPDATED' && e.data.url === url) {
        cleanup();
        resolve();
      }
    };

    const cleanup = () => {
      clearTimeout(timer);
      navigator.serviceWorker.removeEventListener('message', onMessage);
    };

    const timer = window.setTimeout(() => {
      cleanup();
      reject(new Error('Timed out waiting for cache update'));
    }, timeoutMs);

    navigator.serviceWorker.addEventListener('message', onMessage);
  });

  // Send the file to the service worker
  sw.postMessage(
    {
      type: 'UPDATE_CACHE',
      url,
      arrayBuffer,
      contentType: file.type,
    },
    [arrayBuffer]
  );

  await waitForCacheUpdate;
}

/**
 * Clears all cached slides.
 */
export async function clearSlidesCache(): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  const reg = await navigator.serviceWorker.ready;
  const sw = reg.active;

  if (!sw) {
    return;
  }

  sw.postMessage({
    type: 'CLEAR_CACHE',
  });
}
